import * as cx from 'classnames';
import { BZ2_SUBMIT_MESSAGE, VISIBILITY_CHECKBOX_LABEL } from 'mk/autogenerated/translations/MessageForm.79f03affa69318d0d255a9fde78df05d'
import { Checkbox } from 'mk/forms/Checkbox';
import { EmojiPicker } from 'mk/forms/EmojiPicker';
import { FakeForm } from 'mk/forms/FakeForm';
import { Form } from 'mk/forms/Form';
import { formActions } from 'mk/forms/formActions';
import { FormControls } from 'mk/forms/FormControls';
import { OwnProps as PhotoPreviewOwnProps } from 'mk/forms/PhotoPreview';
import { PhotoUpload } from 'mk/forms/PhotoUpload';
import { SnippetField } from 'mk/forms/SnippetField';
import StickerSelect from 'mk/forms/StickerSelect';
import { Textarea } from 'mk/forms/Textarea';
import { Context } from 'mk/interfaces';
import * as React from 'react';

// keep in sync with photoblog/constants.py
const POST_VISIBILITY_PUBLIC = 0;
const POST_VISIBILITY_AUTHENTICATED = 1;

type Action = (() => void) | string;

interface OwnProps {
    context: Context;
    className?: string;
    placeholder?: string;
    buttonValue?: string;
    action?: Action;
    additionalElements?: React.ReactNode;
    additionalFormFields?: React.ReactNode;
    additionalFormActions?: React.ReactNode;

    photos?: boolean;
    stickers?: boolean;
    emojis?: boolean;
    snippets?: boolean;
    visibility?: boolean;

    rows?: number;
    autofocus?: boolean;
    initialMessage?: string;

    messageRequired?: boolean;
    messageLabel?: string;
    initialCursorAtBeginning?: boolean;

    photoType?: string;
    previewPhotoSize?: string;
    multiplePhotos?: boolean;
    initialPhotos?: string[];
    customPhotoPreview?: React.ComponentType<PhotoPreviewOwnProps>;

    defaultVisibility?: number;

    onCancel?();
    onClean?(fieldValues, fieldErrors, form);
    onSubmit?(formValues, form);
    onError?(formState, form);
    onSubmitSuccess?(formState, form);
    onSubmitFailure?(formState, form);
}

interface IForm {
    getFieldRef(name: string);
}

export class MessageForm extends React.Component<OwnProps> {
    public static defaultProps = {
        placeholder: '',

        photos: false,
        emojis: false,
        stickers: false,
        snippets: false,
        visibility: false,

        rows: 2,
        autofocus: false,

        messageRequired: true,
        initialCursorAtBeginning: false,

        defaultVisibility: 1,
    };

    private form: IForm;

    public render() {
        const buttonValue = this.props.buttonValue || BZ2_SUBMIT_MESSAGE;
        const FormComponent = this.props.context.isFake ? FakeForm : Form;

        const messageComponent = (
            <Textarea
                name="message"
                rows={this.props.rows}
                placeholder={this.props.placeholder}
                initial={this.props.initialMessage}
                required={this.props.messageRequired}
                label={this.props.messageLabel}
                autofocus={this.props.autofocus}
                onChange={this.checkForSnippets}
                initialCursorAtBeginning={this.props.initialCursorAtBeginning}
            />
        );

        return (
            <div className="message-form-wrapper">
                {this.props.additionalElements}
                <FormComponent
                    context={this.props.context}
                    action={this.props.action}
                    className={cx('g-form-message-form', 'message-form', this.props.className)}
                    ref={(r) => this.form = r}
                    onClean={this.onClean}
                    onSubmit={this.props.onSubmit}
                    onError={this.props.onError}
                    onSubmitSuccess={this.props.onSubmitSuccess}
                    onSubmitFailure={this.props.onSubmitFailure}
                >

                    {this.props.additionalFormFields}

                    {messageComponent}

                    <div className="form-actions">
                        <div className="form-actions-left">
                            {this.props.photos && (
                                <PhotoUpload
                                    context={this.props.context}
                                    name="photos"
                                    photoType={this.props.photoType}
                                    previewPhotoSize={this.props.previewPhotoSize}
                                    multiple={this.props.multiplePhotos}
                                    initialPhotos={this.props.initialPhotos}
                                    customPreview={this.props.customPhotoPreview}
                                    onChange={this.photoUploadChanged}
                                    className={this.props.stickers ? 'with-stickers' : ''}
                                />
                            )}
                            {this.props.stickers && (
                                <StickerSelect
                                     context={this.props.context}
                                     name="sticker"
                                     className={this.props.photos ? 'with-photos' : ''}
                                     onChange={this.stickerSelectChanged}
                                />
                            )}
                            {this.props.emojis && (
                                <EmojiPicker
                                    context={this.props.context}
                                    onSelect={this.insertToMessage}
                                />
                            )}
                            {this.props.snippets && (
                                <SnippetField
                                    context={this.props.context}
                                    name="snippet"
                                    onChange={this.snippetFieldChanged}
                                />
                            )}
                        </div>
                        <div className="form-actions-right">
                            {this.props.additionalFormActions}
                            {this.props.visibility && (
                                <Checkbox
                                    name="visibility"
                                    className="visibility-checkbox"
                                    initial={!this.props.defaultVisibility}
                                    itemLabel={VISIBILITY_CHECKBOX_LABEL}
                                />
                            )}
                            <div className="controls-wrapper">
                                <FormControls
                                    submitText={buttonValue}
                                    onCancel={this.props.onCancel
                                        ? this.onCancel
                                        // schovaj cancel button, ak props.onCancel je nedefinovane
                                        : null
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </FormComponent>
            </div>
        );
    }

    private onCancel = () => {
        if (!this.props.onCancel) {
            return undefined;
        }

        if (this.props.context.isFake) {
            return this.props.onCancel;
        }

        this.props.context.executeAction(formActions.formClear);
        this.props.onCancel();
    };

    private checkForSnippets = (message) => {
        if (!this.props.snippets) {
            return false;
        }

        if (this.props.photos && this.form.getFieldRef('photos').hasValue()) {
            return;
        }

        this.form.getFieldRef('snippet').checkForSnippets(message);
    };

    private photoUploadChanged = (value) => {
        if (!value.length || !this.form) {
            return;
        }

        if (this.props.snippets) {
            this.form.getFieldRef('snippet').clearSnippet();
        }
        if (this.props.stickers) {
            this.form.getFieldRef('sticker').clear();
        }
    };

    private snippetFieldChanged = (value) => {
        if (!value || !this.form) {
            return;
        }

        if (this.props.photos) {
            this.form.getFieldRef('photos').clear();
        }
        if (this.props.stickers) {
            this.form.getFieldRef('sticker').clear();
        }
    };

    private stickerSelectChanged = (value) => {
        if (!value || !this.form) {
            return;
        }

        if (this.props.snippets) {
            this.form.getFieldRef('snippet').clearSnippet();
        }
        if (this.props.photos) {
            this.form.getFieldRef('photos').clear();
        }
    };

    private insertToMessage = (text) => {
        const message = this.form.getFieldRef('message');
        const cursorPosition = message.insert(text);

        message.setFocus(() => message.moveCursorToPosition(cursorPosition));
    };

    private onClean = (fieldValues, fieldErrors, form) => {
        if (this.props.visibility) {
            fieldValues.visibility = fieldValues.visibility ? POST_VISIBILITY_PUBLIC : POST_VISIBILITY_AUTHENTICATED;
        }

        const afterClean = this.props.onClean ? this.props.onClean(fieldValues, fieldErrors, form) : {};
        return {
            fieldValues,
            ...afterClean,
        };
    };
}
